import {
  AIIconPicker,
  AITextAreaInput,
  Button,
  Dialog,
  RoleVisibilitySelect,
  SelectV2,
  TextInput,
  type IconoirIconName,
  type SelectOption,
} from "@vellum/ui";
import { suggestIconNames, vellumApiSdk } from "@vellum/micro-app-auth";
import {
  LinkSection,
  type HubFolderItem,
  type LinkSchema,
} from "@vellum/vellum-api";
import { useFormik } from "formik";
import * as yup from "yup";

import { SectionPicker } from "@/components/SectionPicker";

/** The folder select's value for "directly in the section". */
const NO_FOLDER = "";

export interface LinkFormValues {
  title: string;
  url: string;
  description: string;
  section: LinkSection;
  icon: string | null;
  visible_to_roles: string[];
  /** A folder id, or `NO_FOLDER`. */
  folder_id: string;
}

const validationSchema = yup.object({
  title: yup.string().trim().required("Title is required"),
  url: yup
    .string()
    .trim()
    .url("Enter a full URL, including https://")
    .required("URL is required"),
  description: yup.string().trim(),
  section: yup
    .string()
    .oneOf(Object.values(LinkSection))
    .required("Section is required"),
});

interface LinkFormDialogProps {
  isOpen: boolean;
  /** The link being edited, or null when creating a new one. */
  link: LinkSchema | null;
  /** Section the "Add link" card belongs to; seeds a new link's section. */
  defaultSection: LinkSection;
  /** Folder a new link starts in, when it's added from inside a folder. */
  defaultFolderId?: string | null;
  /** Every folder; the select offers the ones in the link's section. */
  folders: HubFolderItem[];
  onClose: () => void;
  onSubmit: (values: LinkFormValues) => Promise<void>;
}

export function LinkFormDialog({
  isOpen,
  link,
  defaultSection,
  defaultFolderId = null,
  folders,
  onClose,
  onSubmit,
}: LinkFormDialogProps) {
  const formik = useFormik<LinkFormValues>({
    enableReinitialize: true,
    initialValues: {
      title: link?.title ?? "",
      url: link?.url ?? "",
      description: link?.description ?? "",
      section: link?.section ?? defaultSection,
      icon: link?.icon ?? null,
      visible_to_roles: link?.visible_to_roles ?? [],
      folder_id: (link ? link.folder_id : defaultFolderId) ?? NO_FOLDER,
    },
    validationSchema,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  const errorFor = (field: keyof LinkFormValues) =>
    formik.touched[field]
      ? (formik.errors[field] as string | undefined)
      : undefined;

  const urlIsOpenable = Boolean(formik.values.url) && !formik.errors.url;

  const folderOptions: SelectOption<string>[] = [
    { value: NO_FOLDER, label: "No folder" },
    ...folders
      .filter((folder) => folder.section === formik.values.section)
      .map((folder) => ({ value: folder.id, label: folder.title })),
  ];

  // A folder only holds links from its own section, so switching sections
  // drops a folder choice that no longer fits.
  const changeSection = (section: LinkSection) => {
    void formik.setFieldValue("section", section);
    const folder = folders.find(
      (candidate) => candidate.id === formik.values.folder_id,
    );
    if (folder && folder.section !== section) {
      void formik.setFieldValue("folder_id", NO_FOLDER);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={link ? "Edit link" : "Add link"}
    >
      <form onSubmit={formik.handleSubmit} className="mt-4 flex flex-col gap-4">
        <TextInput
          id="title"
          name="title"
          label="Title"
          value={formik.values.title}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          color={errorFor("title") ? "error" : undefined}
          errortext={errorFor("title")}
        />
        <TextInput
          id="url"
          name="url"
          label="URL"
          placeholder="https://example.com"
          value={formik.values.url}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          color={errorFor("url") ? "error" : undefined}
          errortext={errorFor("url")}
          endingAdornment={
            <button
              type="button"
              aria-label="Open URL in new tab"
              title="Open in new tab"
              disabled={!urlIsOpenable}
              onClick={() =>
                window.open(formik.values.url.trim(), "_blank", "noopener")
              }
              className="flex items-center text-gray-400 hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-gray-400"
            >
              <span
                className="iconoir-open-new-window text-lg"
                aria-hidden="true"
              />
            </button>
          }
        />
        <AITextAreaInput
          id="description"
          name="description"
          label="Description"
          rows={2}
          className="resize-y"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          onSuggest={async (currentValue) => {
            const result = await vellumApiSdk.suggestDescription({
              body: {
                subject: formik.values.title.trim() || "an internal hub link",
                context: formik.values.url.trim() || null,
                current_text: currentValue.trim() || null,
              },
            });
            if (result.data === undefined) {
              throw new Error("Suggestion request failed");
            }
            return result.data.suggestion;
          }}
          onSuggestion={(suggestion) =>
            void formik.setFieldValue("description", suggestion)
          }
        />
        <SectionPicker value={formik.values.section} onChange={changeSection} />
        <SelectV2<string>
          name="folder_id"
          label="Folder"
          options={folderOptions}
          value={
            folderOptions.find(
              (option) => option.value === formik.values.folder_id,
            ) ?? folderOptions[0]
          }
          onChange={(option) =>
            void formik.setFieldValue("folder_id", option?.value ?? NO_FOLDER)
          }
        />
        <AIIconPicker
          label="Icon"
          value={formik.values.icon}
          onChange={(icon: IconoirIconName | null) =>
            void formik.setFieldValue("icon", icon)
          }
          subject={formik.values.title}
          context={formik.values.description || formik.values.url}
          onSuggest={suggestIconNames}
        />
        <RoleVisibilitySelect
          itemLabel="link"
          value={formik.values.visible_to_roles}
          onChange={(roles) =>
            void formik.setFieldValue("visible_to_roles", roles)
          }
        />
        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={formik.isSubmitting}>
            {link ? "Save changes" : "Add link"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
